import { useMutation } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type { MarkAPInvoicesPaidInput } from '@erp/rpc';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

export function useMarkAPInvoicesPaid() {
  const gt = useGT();
  const { showToast } = useToast();
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn(
    (client, { invoiceIds }: { invoiceIds: string[] }) => [
      client.accountsPayable.invoices.list.key(),
      client.accountsPayable.invoices.get.key(),
      client.finance.payments.list.key(),
      ...invoiceIds.map((id) =>
        client.accountsPayable.invoices.get.queryKey({ input: { id } }),
      ),
    ],
  );

  return useMutation(
    orpc.accountsPayable.invoices.markPaid.mutationOptions({
      onSuccess: (data, variables) => {
        invalidateRpcQueryFn({ invoiceIds: variables.invoiceIds });

        if (data.markedPaid === 0) {
          const reason = data.failures[0]?.error;
          showToast({
            title: gt('Could not mark as paid', {
              context:
                'toast title when recording an AP invoice as paid outside Gently fails',
            }),
            message:
              data.failures.length > 1
                ? gt('{count} invoices could not be marked paid. {reason}', {
                    context:
                      'toast when every selected AP invoice failed to be marked paid',
                    count: String(data.failures.length),
                    reason: reason ?? '',
                  })
                : (reason ??
                  gt('Failed to mark invoice as paid', {
                    context: 'toast when marking one AP invoice paid fails',
                  })),
            color: 'error',
            icon: 'x',
          });
          return;
        }

        if (data.failures.length > 0) {
          showToast({
            title: gt('Partly marked as paid', {
              context:
                'toast title when some AP invoices were recorded paid and some were not',
            }),
            message: gt(
              'Marked {marked} as paid. {failed} could not be marked.',
              {
                context: 'toast when a mark-as-paid request partially succeeds',
                marked: String(data.markedPaid),
                failed: String(data.failures.length),
              },
            ),
            color: 'warning',
            icon: 'check',
          });
          return;
        }

        showToast({
          title: gt('Marked as paid', {
            context:
              'toast title after recording AP invoices paid outside Gently',
          }),
          message:
            data.markedPaid === 1
              ? gt('The invoice was recorded as paid outside Gently.', {
                  context: 'toast after marking one AP invoice paid',
                })
              : gt('{count} invoices were recorded as paid outside Gently.', {
                  context: 'toast after marking several AP invoices paid',
                  count: String(data.markedPaid),
                }),
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: gt('Could not mark as paid', {
            context:
              'toast title when the mark-as-paid request fails before invoices are updated',
          }),
          message:
            error.message ||
            gt('Failed to mark invoice as paid', {
              context: 'fallback toast when marking an AP invoice paid fails',
            }),
          color: 'error',
          icon: 'x',
        });
      },
    }),
  );
}

export type { MarkAPInvoicesPaidInput };
